import { Behavior, AttachmentRule } from './types';

export class HealthBehavior extends Behavior {
  private maxHealth: number = 100;
  private currentHealth: number = 100;
  private healthSphere: HTMLElement | null = null;
  private sphereSize: number = 0.2;
  private sphereOffset: number = 2.0; // Distance above the parent element

  static getAttachmentRules(): AttachmentRule[] {
    return [
      { type: 'class', selector: 'health' },
      { type: 'class', selector: 'has-health' },
      // Can also attach to specific elements like characters
      { type: 'element', selector: 'm-character' }
    ];
  }

  onAttach(): void {
    console.log('HealthBehavior attached to:', this.element);
    
    // Initialize health values from attributes if present
    const maxHealthAttr = this.element.getAttribute('max-health');
    if (maxHealthAttr) {
      const maxHealth = parseFloat(maxHealthAttr);
      if (!isNaN(maxHealth) && maxHealth > 0) {
        this.maxHealth = maxHealth;
      }
    }

    const currentHealthAttr = this.element.getAttribute('current-health');
    if (currentHealthAttr) {
      const currentHealth = parseFloat(currentHealthAttr);
      if (!isNaN(currentHealth) && currentHealth >= 0) {
        this.currentHealth = Math.min(currentHealth, this.maxHealth);
      }
    }

    // Initialize sphere size from attribute if present
    const sphereSizeAttr = this.element.getAttribute('health-sphere-size');
    if (sphereSizeAttr) {
      const size = parseFloat(sphereSizeAttr);
      if (!isNaN(size) && size > 0) {
        this.sphereSize = size;
      }
    }

    // Initialize sphere offset from attribute if present
    const sphereOffsetAttr = this.element.getAttribute('health-sphere-offset');
    if (sphereOffsetAttr) {
      const offset = parseFloat(sphereOffsetAttr);
      if (!isNaN(offset)) {
        this.sphereOffset = offset;
      }
    }

    // Create and position the health sphere
    this.createHealthSphere();
    this.updateHealthDisplay();
    this.updateHealthClasses();
  }

  onDetach(): void {
    console.log('HealthBehavior detached from:', this.element);
    
    // Remove the health sphere
    if (this.healthSphere && this.healthSphere.parentNode) {
      this.healthSphere.parentNode.removeChild(this.healthSphere);
    }
    this.healthSphere = null;

    // Remove health classes
    this.element.classList.remove('alive', 'dead');

    // Remove methods from element
    delete (this.element as any).takeDamage;
    delete (this.element as any).heal;
    delete (this.element as any).getHealth;
  }

  private createHealthSphere(): void {
    if (this.healthSphere) {
      return; // Already created
    }

    // Create a sphere element to represent health
    this.healthSphere = document.createElement('m-sphere');
    this.healthSphere.setAttribute('radius', this.sphereSize.toString());
    this.healthSphere.setAttribute('color', 'green');
    
    // Position the sphere above the parent element
    this.updateSpherePosition();
    
    // Add the sphere to the parent element
    this.element.appendChild(this.healthSphere);
  }

  private updateSpherePosition(): void {
    if (!this.healthSphere) return;

    // Position the sphere above the parent element
    this.healthSphere.setAttribute('x', '0');
    this.healthSphere.setAttribute('y', this.sphereOffset.toString());
    this.healthSphere.setAttribute('z', '0');
  }

  private updateHealthDisplay(): void {
    if (!this.healthSphere) return;

    // Calculate health percentage
    const healthPercentage = this.currentHealth / this.maxHealth;
    
    // Determine color based on health percentage
    let color: string;
    if (healthPercentage > 0.7) {
      color = 'green';
    } else if (healthPercentage > 0.4) {
      color = 'yellow';
    } else if (healthPercentage > 0.2) {
      color = 'orange';
    } else if (healthPercentage > 0) {
      color = 'red';
    } else {
      color = 'darkred';
    }

    this.healthSphere.setAttribute('color', color);
    
    // Update element attributes for external access
    this.element.setAttribute('current-health', this.currentHealth.toString());
    this.element.setAttribute('max-health', this.maxHealth.toString());
    this.element.setAttribute('health-percentage', (healthPercentage * 100).toFixed(1));
  }

  private updateHealthClasses(): void {
    if (this.currentHealth <= 0) {
      this.element.classList.remove('alive');
      this.element.classList.add('dead');
    } else {
      this.element.classList.remove('dead');
      this.element.classList.add('alive');
    }
  }

  public takeDamage(amount: number): void {
    if (amount < 0) {
      console.warn('Damage amount cannot be negative. Use heal() instead.');
      return;
    }

    const previousHealth = this.currentHealth;
    this.currentHealth = Math.max(0, this.currentHealth - amount);
    
    //console.log(`${this.element.tagName} took ${amount} damage. Health: ${previousHealth} -> ${this.currentHealth}`);
    
    this.updateHealthDisplay();
    this.updateHealthClasses();

    // Dispatch custom event for damage taken
    const damageEvent = new CustomEvent('damage-taken', {
      detail: {
        amount: amount,
        previousHealth: previousHealth,
        currentHealth: this.currentHealth,
        isDead: this.currentHealth <= 0
      },
      bubbles: true
    });
    this.element.dispatchEvent(damageEvent);
  }

  public heal(amount: number): void {
    if (amount < 0) {
      console.warn('Heal amount cannot be negative. Use takeDamage() instead.');
      return;
    }

    const previousHealth = this.currentHealth;
    this.currentHealth = Math.min(this.maxHealth, this.currentHealth + amount);
    
    console.log(`${this.element.tagName} healed ${amount}. Health: ${previousHealth} -> ${this.currentHealth}`);
    
    this.updateHealthDisplay();
    this.updateHealthClasses();

    // Dispatch custom event for healing
    const healEvent = new CustomEvent('healed', {
      detail: {
        amount: amount,
        previousHealth: previousHealth,
        currentHealth: this.currentHealth,
        isAlive: this.currentHealth > 0
      },
      bubbles: true
    });
    this.element.dispatchEvent(healEvent);
  }

  tick(deltaTime: number): void {
    // Check if health attributes have been modified externally
    const currentHealthAttr = this.element.getAttribute('current-health');
    if (currentHealthAttr) {
      const newHealth = parseFloat(currentHealthAttr);
      if (!isNaN(newHealth) && newHealth !== this.currentHealth) {
        this.currentHealth = Math.max(0, Math.min(newHealth, this.maxHealth));
        this.updateHealthDisplay();
        this.updateHealthClasses();
      }
    }

    const maxHealthAttr = this.element.getAttribute('max-health');
    if (maxHealthAttr) {
      const newMaxHealth = parseFloat(maxHealthAttr);
      if (!isNaN(newMaxHealth) && newMaxHealth > 0 && newMaxHealth !== this.maxHealth) {
        this.maxHealth = newMaxHealth;
        // Ensure current health doesn't exceed new max
        this.currentHealth = Math.min(this.currentHealth, this.maxHealth);
        this.updateHealthDisplay();
      }
    }

    // Update sphere position in case parent element moved
    this.updateSpherePosition();
  }
}
